/**
 * Environment-scoped WABA API Service
 *
 * Service layer for environment-scoped WABA (WhatsApp Business Account) API calls.
 * Each environment can have at most one WABA connection.
 * Only organisation owners can manage WABA connections.
 */

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Service for environment-scoped WABA API calls
 */
class EnvironmentWabaService {

  /**
   * Get WABA connection for an environment
   *
   * @param {string} orgId - Organisation ID
   * @param {string} envId - Environment ID
   * @param {Function} authenticatedFetch - Authenticated fetch function from AuthContext
   * @returns {Promise<Object|null>} WABA connection details or null if not connected
   */
  async getConnection(orgId, envId, authenticatedFetch) {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${orgId}/environments/${envId}/waba`
      );

      if (response.status === 404) {
        return null; // No WABA connected to this environment
      }

      if (!response.ok) {
        throw new Error('Failed to fetch WABA connection');
      }

      return await response.json();
    } catch (error) {
      if (error.message?.includes('404')) {
        return null;
      }
      console.error('Error fetching WABA connection:', error);
      throw error;
    }
  }

  /**
   * Connect a WABA to an environment (from an Embedded Signup result)
   *
   * @param {string} orgId - Organisation ID
   * @param {string} envId - Environment ID
   * @param {Object} wabaData - WABA connection data from Embedded Signup
   * @param {string} wabaData.wabaId - WhatsApp Business Account ID
   * @param {string} wabaData.code - Embedded Signup authorization code (exchanged server-side)
   * @param {string} [wabaData.phoneNumberId] - Onboarded phone number ID
   * @param {string} [wabaData.accountType='production'] - Account type ('production' | 'test' | 'coexistence')
   * @param {string} [wabaData.scope] - OAuth scope
   * @param {Function} authenticatedFetch - Authenticated fetch function from AuthContext
   * @param {{replace?: boolean}} [options] - replace: put this WABA in place of the one the
   *   environment has (PUT); its flows go back to draft and redeploy on the new number
   * @returns {Promise<Object>} WABA connection details
   */
  async connectWaba(orgId, envId, wabaData, authenticatedFetch, { replace = false } = {}) {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${orgId}/environments/${envId}/waba`,
        {
          method: replace ? 'PUT' : 'POST',
          body: JSON.stringify(wabaData)
        }
      );

      if (response.status === 403) {
        throw new Error(replace
          ? 'Only owners and admins can change the number'
          : 'Only organization owners can connect WABAs');
      }

      if (response.status === 409) {
        throw new Error('Environment already has a WABA connection');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to connect WABA');
      }

      return await response.json();
    } catch (error) {
      console.error('Error connecting WABA:', error);
      throw error;
    }
  }

  /**
   * Refresh OAuth token for environment's WABA connection
   *
   * @param {string} orgId - Organisation ID
   * @param {string} envId - Environment ID
   * @param {string} accessToken - New access token
   * @param {number} expiresIn - Token expiry duration in seconds
   * @param {Function} authenticatedFetch - Authenticated fetch function from AuthContext
   * @returns {Promise<Object>} Updated WABA connection details
   */
  async refreshToken(orgId, envId, accessToken, expiresIn, authenticatedFetch) {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${orgId}/environments/${envId}/waba/refresh-token`,
        {
          method: 'POST',
          body: JSON.stringify({ accessToken, expiresIn })
        }
      );

      if (response.status === 403) {
        throw new Error('Only organization owners can refresh tokens');
      }

      if (response.status === 404) {
        throw new Error('No WABA connection found for this environment');
      }

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      return await response.json();
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const environmentWabaService = new EnvironmentWabaService();

// Also export the class for testing purposes
export { EnvironmentWabaService };
